import { useEffect, useState } from "react";
import createUseContext from "constate";
import flatten from "lodash/flatten";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";

import Helpers from "../../services/helpers";
import useGlobal from "../../store";
import { generateCardTitle } from "../../utils/cardList";
import { CHART_LINES, CHARTS, ZERO_REFERENCE_LINE } from "../charts/constants";
import { percentageGrowthModel, TREND_LINE_CONTROL_MODELS } from "../charts/lineChart.assets";
import { useSearchContext } from "./searchcontext.ts";

function useGraph() {
  const timespans = ["day", "week", "month"];
  const [resultsBy, setResultsBy] = useState({
    valuePerDay: "day",
    numberOfSales: "day",
    totalSales: "day",
    percentageGrowthChart: "day",
  });
  const [lineType, setLineType] = useState({
    valuePerDay: "both",
    numberOfSales: "both",
    totalSales: "both",
    percentageGrowthChart: null,
  });
  const [lines, setLines] = useState(getInitialLines());
  const [points, setPoints] = useState(getInitialPoints());
  const [colors, setColors] = useState({});
  const [selected, setSelected] = useState(CHARTS.valuePerDay);
  const [heading, setHeading] = useState("");
  const [cardsVisibility, setCardsVisibility] = useState({});
  const [globalState] = useGlobal();
  const { parsedCards: newCardResults } = useSearchContext();

  function getTimespansData() {
    return { day: [], week: [], month: [] };
  }

  function getInitialLines() {
    return {
      valuePerDay: [],
      numberOfSales: [],
      totalSales: [],
      percentageGrowthChart: [],
    };
  }

  function getInitialPoints() {
    return {
      valuePerDay: getTimespansData(),
      numberOfSales: getTimespansData(),
      totalSales: getTimespansData(),
      percentageGrowthChart: getTimespansData(),
    };
  }

  const createLine = ({
    popCount,
    popCountLastUpdated,
    serialNumber,
    image,
    dataKey,
    name,
    visible,
    stroke,
    type,
    strokeWidth,
    query,
    nameWOGrade,
    grade,
    ...props
  }) => {
    return {
      isTrend: false,
      image,
      population_count: popCount,
      population_last_updated: popCountLastUpdated,
      serial_number: serialNumber,
      type,
      name,
      dataKey,
      stroke: stroke || Helpers.getRandomWeightedHexColorByKey(dataKey),
      visible,
      showDots: true,
      strokeWidth: strokeWidth || 4,
      query,
      nameWOGrade,
      grade,
      ...props,
    };
  };

  const createTrendLine = ({
    popCount,
    popCountLastUpdated,
    serialNumber,
    image,
    dataKey,
    name,
    visible,
    stroke,
    type,
    strokeWidth,
    query,
    nameWOGrade,
    grade,
  }) => {
    return {
      isTrend: true,
      image,
      population_count: popCount,
      population_last_updated: popCountLastUpdated,
      serial_number: serialNumber,
      type,
      name,
      dataKey,
      stroke: stroke || Helpers.getRandomWeightedHexColorByKey(dataKey),
      visible,
      showDots: false,
      strokeWidth: strokeWidth || 2,
      query,
      nameWOGrade,
      grade,
    };
  };

  const getAllLinesByCardID = (card_id) => {
    const allLines = flatten(Object.keys(lines).map((lineKey) => lines[lineKey]));
    const cardLines = allLines.filter((line) => line.dataKey.startsWith(card_id + "_"));
    return cardLines;
  };

  const getLinesByCardID = (card_id) => {
    const cardLines = lines[selected].filter((line) => line.dataKey.startsWith(card_id + "_"));
    return cardLines;
  };

  const toggleLinesByCardId = (cardId) => {
    const models = cloneDeep(TREND_LINE_CONTROL_MODELS);
    models.percentageGrowthChart = percentageGrowthModel;

    const newLines = Object.entries(lines).reduce((acc, [key, values]) => {
      let defaultVisibleLines = [];
      if (models[key]) {
        defaultVisibleLines = models[key]
          .find(({ id }) => id === lineType[key])
          .setVisibility.reduce((acc, option) => {
            if (option.value) {
              return acc.concat(option.id);
            }

            return acc;
          }, []);
      }

      acc[key] = values.map((line) => {
        if (line.dataKey.startsWith(`${cardId}`)) {
          if (cardsVisibility[cardId].visible) {
            line.visible = false;
          } else {
            const lineId = line.dataKey.split("_").slice(1).join("_");
            if (defaultVisibleLines.includes(lineId)) {
              line.visible = true;
            }
          }
        }

        return line;
      });

      return acc;
    }, lines);

    setLines(newLines);

    setCardsVisibility((prev) =>
      Object.entries(prev).reduce((acc, [key, values]) => {
        acc[key] = values;

        if (key === cardId) {
          acc[key].visible = !values.visible;
        }

        return acc;
      }, {}),
    );
  };

  const toggleLineByType = (type) => {
    const checkLines = lines[selected].filter((line) => line.dataKey.endsWith("_" + type) && line.visible);
    //no lines of that type visible
    const newLines = { ...lines };
    if (checkLines.length === 0) {
      for (let line of newLines[selected]) {
        if (line.dataKey.endsWith("_" + type)) {
          line.visible = true;
        }
      }
    } else {
      for (let line of newLines[selected]) {
        if (line.dataKey.endsWith("_" + type)) {
          line.visible = false;
        }
      }
    }

    setLines(newLines);
  };

  const setLinesVisibilityByType = (type, visible) => {
    const newLines = { ...lines };

    for (let line of newLines[selected]) {
      const [cardId] = line.dataKey.split("_");

      if (!cardsVisibility[cardId].visible) continue;

      if (line.dataKey.endsWith("_" + type)) {
        line.visible = visible;
      }
    }

    setLines(newLines);
  };

  const isLineVisible = (line_id) => {
    const theLine = lines[selected].find((line) => line.dataKey === line_id);
    return theLine ? theLine.visible : false;
  };

  const toggleLine = (line_id) => {
    let newLines = { ...lines };

    const index = newLines[selected].findIndex((line) => line.dataKey === line_id);
    if (index > -1) {
      const lineId = newLines[selected][index].dataKey.split("_").slice(1).join("_");

      newLines[selected][index].visible = !newLines[selected][index].visible;
      setLines(newLines);

      if (selected === "valuePerDay" && lineType[selected] === "both" && lineId === "avg") {
        toggleLine(`${line_id}_trend`);
      }
    }
  };

  const updateLineColor = (line_id, color) => {
    let newLines = { ...lines };
    const index = newLines[selected].findIndex((line) => line.dataKey === line_id);
    if (index > -1) {
      newLines[selected][index].stroke = color;
      setLines(newLines);
    }
  };

  const setCardActiveAlert = (targetCardId) => {
    Object.entries(lines).reduce((acc, [key, values]) => {
      acc[key] = values.map((item) => {
        const [cardId] = item.dataKey.split("_");

        if (Number(cardId) === Number(targetCardId) && item.hasAlert !== undefined) {
          item.hasAlert = true;
        }

        return item;
      });

      return acc;
    }, lines);
  };

  const calculateData = (newCardResults, startDate, endDate, shouldRecalculate = false) => {
    if (shouldRecalculate) {
      newCardResults = Helpers.parseCards(newCardResults, startDate, endDate, globalState);
    }

    const newLines = getInitialLines();
    const pointsByChart = getInitialPoints();
    let colorRotation = 0;
    const newColors = {};

    newCardResults.sort(Helpers.sortCardsByNameThenGrade).forEach((card) => {
      const image = Helpers.pickMmImage(card) || null;
      const smallImage = Helpers.pickMmImage(card, "S") || null;
      const color = card.color || Helpers.getColorFromPalette(colorRotation, newCardResults.length);
      colorRotation++;
      newColors[card.id] = color;

      const name = card.chartLabel || generateCardTitle(card, { hideSport: true });
      const nameWOGrade = generateCardTitle(card, { hideGrade: true, hideSport: true });
      const grade = (card.grade && (card.grade.abbreviation || card.grade.name)) || "";
      const isRookieCard = card.player.rookie_years ? card.player.rookie_years.includes(card.card_set.year) : false;

      Object.keys(CHARTS).forEach((chart) => {
        if (CHART_LINES[chart]) {
          CHART_LINES[chart].forEach(({ lineId, visible, isTrend }) => {
            const dataKey = `${card.id}_${lineId}`;
            const lineProps = {
              isRookieCard,
              nameWOGrade,
              grade,
              popCount: card.population_count,
              popCountLastUpdated: card.population_last_updated,
              serialNumber: card.serial_number,
              image,
              smallImage,
              dataKey,
              name,
              visible,
              stroke: color,
              query: card.query,
              hasAlert: card.has_alert || false,
            };

            let fn = createLine;

            if (isTrend) {
              lineProps.dataKey = dataKey + "_trend";
              lineProps.type = "monotone";
              lineProps.strokeWidth = 1;
              fn = createTrendLine;
            }

            if (!newLines[chart]) newLines[chart] = [];
            newLines[chart].push(fn(lineProps));
          });
        }

        for (const timespan of timespans) {
          if (!pointsByChart[chart][timespan]) pointsByChart[chart][timespan] = [];
          if (card.card_results_data) {
            pointsByChart[chart][timespan] = pointsByChart[chart][timespan].concat(card.card_results_data[timespan]);
          }
        }
      });
    });

    Object.keys(CHARTS).forEach((chart) => {
      for (const timespan of timespans) {
        pointsByChart[chart][timespan] = Helpers.mergeData(pointsByChart[chart][timespan]);
      }
    });

    newCardResults.forEach((card) => {
      const trendFuncs = getTrendLineFuncsByCard(card.id, pointsByChart);
      const trendLineData = getTimespansData();

      Object.keys(CHARTS).forEach((chart) => {
        for (const timespan of timespans) {
          if (pointsByChart[chart][timespan].length > 1) {
            for (let x = 0; x < pointsByChart[chart][timespan].length; x++) {
              const y = trendFuncs[chart][timespan](pointsByChart[chart][timespan][x].sortValue);
              if (y >= 0) trendLineData[timespan].push({ x, y });
            }

            if (trendLineData[timespan].length > 0) {
              const trendLine = CHART_LINES[chart].find((line) => line.isTrend);
              const id = `${card.id}_${trendLine.lineId}_trend`;
              const initial = chart === "numberOfSales" ? Math.ceil(trendLineData[timespan].length / 2) : 0;

              pointsByChart[chart][timespan][trendLineData[timespan][initial].x][id] =
                trendLineData[timespan][initial].y;
              pointsByChart[chart][timespan][trendLineData[timespan][trendLineData[timespan].length - 1].x][id] =
                trendLineData[timespan][trendLineData[timespan].length - 1].y;
            }
          }
        }
      });
    });

    for (const timespan of timespans) {
      pointsByChart[CHARTS.percentageGrowthChart][timespan] = pointsByChart[CHARTS.percentageGrowthChart][timespan].map(
        (item) => ({
          ...item,
          [ZERO_REFERENCE_LINE]: 0,
        }),
      );
    }

    setPoints(pointsByChart);
    setColors(newColors);
    setLines(newLines);
    setCardsVisibility(
      newCardResults.reduce(
        (acc, { id }) => ({
          ...acc,
          [id]: {
            visible: true,
          },
        }),
        {},
      ),
    );
  };

  const getTrendLineFuncsByCard = (cardId, sortedCompleteData) => {
    const funcs = {};

    Object.keys(CHARTS).forEach((chart) => {
      for (const timespan of timespans) {
        const data = sortedCompleteData[chart][timespan] || [];

        if (!funcs[chart]) funcs[chart] = {};

        if (!data.length) {
          funcs[chart][timespan] = () => null;
          return;
        }

        const trendLine = CHART_LINES[chart].find((line) => line.isTrend);
        const trendLineId = trendLine.lineId;

        const id = `${cardId}_${trendLineId}`;
        const x = [];
        const x2 = [];
        const xDate = [];
        const y = [];
        const xy = [];
        const firstDate = moment(data[0].sortValue.split("_")[1], "YYYYMMDD");

        for (let i = 0; i < data.length; i++) {
          if (data[i][id]) {
            const date = data[i].sortValue.split("_")[1];
            xDate.push(data[i].sortValue);

            const days = moment(date, "YYYYMMDD").diff(firstDate, "days");
            x.push(days);
            y.push(data[i][id]);
            x2.push(days * days);
            xy.push(days * data[i][id]);
          }
        }

        const n = x.length;
        const ysum = y.reduce((a, b) => a + b, 0);
        const xsum = x.reduce((a, b) => a + b, 0);
        const x2sum = x2.reduce((a, b) => a + b, 0);
        const xysum = xy.reduce((a, b) => a + b, 0);
        const a = n * xysum;
        const b = ysum * xsum;
        const c = n * x2sum;
        const d = xsum * xsum;
        const slope = (a - b) / (c - d);
        const intercept = (ysum - xsum * slope) / n;

        funcs[chart][timespan] = (dateForPoint) => {
          const d = moment(dateForPoint.split("_")[1], "YYYYMMDD").diff(firstDate, "days");
          return d * slope + intercept;
        };
      }
    });

    return funcs;
  };

  const reset = () => {
    setResultsBy({ valuePerDay: "day", numberOfSales: "day", totalSales: "day", percentageGrowthChart: "day" });
    setLineType({ valuePerDay: "both", numberOfSales: "both", totalSales: "both", percentageGrowthChart: null });
    setLines(getInitialLines());
    setPoints(getInitialPoints());
    setHeading("");
  };

  useEffect(() => {
    calculateData(newCardResults);
  }, [newCardResults]);

  return {
    resultsBy,
    setResultsBy,
    lineType,
    setLineType,
    lines,
    setLines,
    points,
    setPoints,
    colors,
    heading,
    setHeading,
    getLinesByCardID,
    getAllLinesByCardID,
    setLinesVisibilityByType,
    toggleLineByType,
    createLine,
    toggleLinesByCardId,
    toggleLine,
    isLineVisible,
    updateLineColor,
    calculateData,
    reset,
    selected,
    setSelected,
    cardsVisibility,
    setCardActiveAlert,
  };
}

const [GraphProvider, useGraphContext] = createUseContext(useGraph);

export { GraphProvider, useGraphContext };
